/**
 * AdjustmentFootnote Component
 * @module components/common/reports/AdjustmentFootnote
 *
 * Displays a footnote banner when average check threshold adjustments are active.
 * Used across R0002, R0003 (both sub-reports), and R0006 to inform users that
 * Last Year Average Check comparison values have been adjusted using configured
 * threshold settings.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { hasAvgCheckAdjustments } from 'services/thresholds/thresholdService';

/**
 * Display footnote when average check threshold adjustments are active
 *
 * @param {Object} props - Component props
 * @param {Array<string>} props.storeIds - Store IDs visible in current report
 * @param {Array<Object>} props.thresholds - Threshold configuration
 * @param {Object} props.storeThresholds - Store threshold assignments
 * @returns {JSX.Element|null} Footnote component or null if no adjustments
 *
 * @example
 * <AdjustmentFootnote
 *   storeIds={['store1', 'store2']}
 *   thresholds={thresholdConfig}
 *   storeThresholds={storeThresholdAssignments}
 * />
 */
function AdjustmentFootnote({ storeIds = [], thresholds = [], storeThresholds = {} }) {
  // Check if any visible stores have non-zero avgCheck high thresholds
  const hasAdjustments = hasAvgCheckAdjustments(storeIds, thresholds, storeThresholds);

  // Don't render if no adjustments are configured
  if (!hasAdjustments) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        backgroundColor: '#e3f2fd',
        borderLeft: '4px solid #2196f3',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}
    >
      <InfoIcon sx={{ color: '#2196f3', fontSize: 20 }} data-testid="InfoIcon" />
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#1565c0' }}>
        * Last Year Average Check comparison values have been adjusted using the configured
        &quot;High&quot; thresholds to align displayed values with current pricing.
        Threshold settings: Admin Tools → ReportAdmin → R0002: Summary Report → Threshold Assignments → Average Check
      </Typography>
    </Box>
  );
}

AdjustmentFootnote.propTypes = {
  /** Array of store IDs visible in the current report */
  storeIds: PropTypes.arrayOf(PropTypes.string),
  /** Threshold configuration object */
  thresholds: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    thresholds: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      lowValue: PropTypes.number,
      highValue: PropTypes.number
    }))
  })),
  /** Store threshold assignments object */
  storeThresholds: PropTypes.object
};

export default AdjustmentFootnote;
